import { CommonModule, Location } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { SvgIconComponent } from 'angular-svg-icon';
import dayjs from 'dayjs';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { Subscription } from 'rxjs';
import { AuthService, UserRole } from '../../../core/services/auth.service';
import { AppState } from '../../../core/store';
import { setSettingsStateAction } from '../../../core/store/actions/settings.action';
import { getSettingsStore } from '../../../core/store/selectors/settings.selector';

interface menuOptInterface {
  name: string;
  link: string[];
  queryParams?: Record<string, string>;
  type: 'All' | 'Danger';
  exact: boolean;
  icon: string;
  subMenuList?: menuOptInterface[];
}

interface dropdownMenuOptInterface {
  name: string;
  link?: string;
  queryParams?: Record<string, string>;
  type: 'All' | 'Danger';
  exact: boolean;
  icon: string;
  roles?: UserRole[];
  subMenuList?: dropdownMenuOptInterface[];
}

@Component({
  selector: 'app-admin-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SvgIconComponent,
    NzAvatarModule,
    NzDropDownModule,
    NzMenuModule,
    NzInputModule,
    NzButtonModule,
  ],
  templateUrl: './admin-header.component.html',
  styleUrl: './admin-header.component.less',
})
export class AdminHeaderComponent implements OnInit, OnDestroy {
  store = inject(Store<AppState>);
  route = inject(ActivatedRoute);
  location = inject(Location);
  authService = inject(AuthService);
  router = inject(Router);

  settingsStoreSub?: Subscription;

  @Input({ required: true }) slug!: string;

  showPopover = false;
  sideMenuCollapsedState = false;

  menuList = signal<menuOptInterface[]>([]);
  dropdownMenuList = signal<dropdownMenuOptInterface[]>([]);

  // get header() {
  //   const pathname = this.location.path();
  //   switch (pathname) {
  //     case '/sadmin':
  //       return 'Dashboard';
  //     case '/sadmin/settings':
  //       return 'Settings';

  //     default:
  //       return 'Dashboard';
  //   }
  // }

  get abbr() {
    const firstName = (this.authService.user()?.userProfile?.user?.firstName as string) || '';
    const lastName = (this.authService.user()?.userProfile?.user?.lastName as string) || '';
    return `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`;
  }

  get fullName() {
    const firstName = (this.authService.user()?.userProfile?.user?.firstName as string) || '';
    const lastName = (this.authService.user()?.userProfile?.user?.lastName as string) || '';
    return `${firstName} ${lastName}`;
  }

  get todayDate() {
    return dayjs().format('DD/MM/YYYY');
  }

  toggleSideMenuCollapsedState() {
    this.store.dispatch(
      setSettingsStateAction({ sideMenuCollapsedState: !this.sideMenuCollapsedState }),
    );
  }

  clickStatusHandler(status: any) {
    if (!status) {
      this.showPopover = false;
    }
  }

  popoverHandler() {
    this.showPopover = !this.showPopover;
  }

  //   logoutHandler() {
  //     this.localService.clearData();
  //     this.router.navigateByUrl('/', { replaceUrl: true });
  //   }
  dropdownMenuClickHandler(item: dropdownMenuOptInterface) {
    if (item.name === 'Log Out') {
      this.authService.clearUserData();
    }

    if (!item.link) {
      return;
    }

    this.router.navigateByUrl(item.link, {
      replaceUrl: true,
    });
  }

  ngOnInit(): void {
    this.settingsStoreSub = this.store.pipe(select(getSettingsStore)).subscribe((settingsStore) => {
      this.sideMenuCollapsedState = settingsStore.sideMenuCollapsedState;
    });

    this.menuList.set(
      [
        {
          name: 'Dashboard',
          link: ['/', this.slug],
          type: 'All',
          exact: true,
          icon: 'assets/svg/dashboard.svg',
        },
        {
          name: 'Users',
          link: ['/', this.slug, 'users'],
          // queryParams: { tab: 'user-management' },
          type: 'All',
          exact: false,
          icon: 'assets/svg/users-group.svg',
        },
      ] /* .filter((val) => {
        if (
          ['Tenants', 'Onboardings'].includes(val.name) &&
          ['superadmin', 'appointment', 'checkin'].includes(
            this.authService.userRole,
          )
        ) {
          return false;
        }

        if (
          [
            'Dashboard',
            'Appointments',
            'Check ins',
            'Visitor Records',
            'Users',
          ].includes(val.name) &&
          ['sysadmin'].includes(this.authService.userRole)
        ) {
          return false;
        }

        if (
          ['Users'].includes(val.name) &&
          !['superadmin'].includes(this.authService.userRole)
        ) {
          return false;
        }

        return true;
      } ),*/,
    );

    const role = this.authService.role();
    const isAllowed = (item: dropdownMenuOptInterface) =>
      !item.roles || item.roles.length === 0 || item.roles.includes(role);

    const baseDropdownMenuList: dropdownMenuOptInterface[] = [
      {
        name: 'Settings',
        type: 'All',
        exact: false,
        icon: 'assets/svg/settings.svg',
        subMenuList: [
          {
            name: 'Users',
            link: '/admin/users',
            type: 'All',
            exact: false,
            icon: 'assets/svg/users-group.svg',
            roles: [UserRole.SystemAdmin, UserRole.Admin],
          },
          {
            name: 'Departments',
            link: '/admin/departments',
            type: 'All',
            exact: false,
            icon: 'assets/svg/folder.svg',
            roles: [UserRole.SystemAdmin, UserRole.Admin, UserRole.Librarian],
          },
          {
            name: 'Cabinets',
            link: '/admin/cabinets',
            type: 'All',
            exact: false,
            icon: 'assets/svg/folder.svg',
            roles: [UserRole.SystemAdmin, UserRole.Admin, UserRole.Librarian],
          },
          {
            name: 'Folders',
            link: '/admin/folders',
            type: 'All',
            exact: false,
            icon: 'assets/svg/folder.svg',
            roles: [UserRole.SystemAdmin, UserRole.Admin, UserRole.Librarian],
          },
        ],
      },
      {
        name: 'Log Out',
        link: '/',
        type: 'Danger',
        exact: true,
        icon: 'assets/svg/logout.svg',
      },
    ];

    const filteredDropdown = baseDropdownMenuList
      .filter(isAllowed)
      .map((item) => {
        if (!item.subMenuList) return item;
        const visibleSubs = item.subMenuList.filter(isAllowed);
        if (visibleSubs.length === 0) return null;
        return { ...item, subMenuList: visibleSubs };
      })
      .filter((item): item is dropdownMenuOptInterface => item !== null);

    this.dropdownMenuList.set(filteredDropdown);
  }

  ngOnDestroy(): void {
    if (this.settingsStoreSub) this.settingsStoreSub.unsubscribe();
  }
}
